import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LoadingBox, PrivilegesChecker, SearchBoxGeneric, GridTable } from "..";
import {
  ActionTypeEnum,
  PageEnum,
  RequestActionModel,
  SearchItemApiResponseModel,
  SearchItemRequestModel,
  SearchItemsTypeEnum,
  SearchFieldRequestModel,
  LookupItemModel,
  LookupTypeEnum,
} from "../../models";
import { searchItems } from "../../serviceBroker/itemApiServiceBroker";
import { getLookupByType } from "../../serviceBroker/lookupApiServiceBroker";
import { isArabicCurrentLanguage } from "../../utils";
import { useSearchBoxGeneric } from "../../hooks";

export const ItemListManager: FC<{
  onActionEvent: (o: RequestActionModel) => void;
  setIsRefresh: Dispatch<SetStateAction<boolean>>;
  isRefresh: boolean;
  isTableLoading: boolean;
  setTableLoading: Dispatch<SetStateAction<boolean>>;
}> = ({
        onActionEvent = () => {},
        setIsRefresh,
        isRefresh = false,
        isTableLoading,
        setTableLoading,
      }) => {
  //#region variables
  const isArabic = isArabicCurrentLanguage();
  const defaultPageSize = 10;
  //#endregion

  //#region lookup state
  const [storeLookup, setStoreLookup] = useState<LookupItemModel[]>([]);
  const [categoryLookup, setCategoryLookup] = useState<LookupItemModel[]>([]);
  //#endregion

  //#region table state
  const [data, setData] = useState<SearchItemApiResponseModel[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  const [searchItemRequest, setSearchItemRequest] =
      useState<SearchItemRequestModel>({
        pageNumber: 1,
        pageSize: defaultPageSize,
        searchType: SearchItemsTypeEnum.ItemName,
        smallestUnit: true,
        searchValue: "",
        barCode: "",
        code: "",
        name: "",
         storeId: null  ,
         categoryId: null,
      });
  //#endregion

  //#region search fields (dynamic based on lookup lists)
  const initialSearchFields: SearchFieldRequestModel[] = useMemo(
      () => [
        {
          key: "storeId",
          value: "",
          label: "Store",
          controlType: "select",
          lookupList: storeLookup,
          triggerSearchOnChange: false,
        },
        {
          key: "categoryId",
          value: "",
          label: "Category",
          controlType: "select",
          lookupList: categoryLookup,
          triggerSearchOnChange: false,
        },
        {
          key: "name",
          value: "",
          label: "Name",
          placeholder: "Enter Name",
          triggerSearchOnChange: true,
        },
        { key: "code", value: "", label: "Code", placeholder: "Enter Code" },
        { key: "barcode", value: "", label: "BarCode", placeholder: "Enter Bar Code" },
      ],
      [storeLookup, categoryLookup]
  );

  const {
    fields: searchFields,
    onChange: handleSearchFieldChange,
    onSearch: handleSearch,
    onReset: handleResetSearch,
    searchedFields,resetVersion,
  } = useSearchBoxGeneric(initialSearchFields);
  //#endregion

  //#region central mapper (ONLY place you update when you add a new field)
  const mapSearchedFieldsToRequest = useCallback(
      (fields: SearchFieldRequestModel[]): Partial<SearchItemRequestModel> => {
        const updated: Partial<SearchItemRequestModel> = {};

        fields.forEach((f) => {
          const v = (f.value ?? "").toString();

          switch (f.key) {
            case "name":
              updated.name = v;
              break;
            case "code":
              updated.code = v;
              break;
            case "barcode":
              updated.barCode = v;
              break;

              // dropdowns (numbers)
            case "storeId":
                updated.storeId = f.value ? Number(f.value) : undefined;
              //(updated as any).storeId = v ? Number(v) : undefined;
              break;
            case "categoryId":
                updated.categoryId = f.value ? Number(f.value) : undefined;
             // (updated as any).categoryId = v ? Number(v) : undefined;
              break;
          }
        });

        return updated;
      },
      []
  );
  //#endregion

  //#region api calls
  const getItems = useCallback(async () => {
      console.log(`res_x_1_1_${JSON.stringify( searchItemRequest)}`)
    const result = await searchItems(searchItemRequest);
    setData(result?.Result || []);
    setTotalRows(result?.TotalRecoredCount || 0);
  }, [searchItemRequest]);
  //#endregion

  //#region paging handlers
  const onCurrentPageChange = async (pageNumber: number) => {
    if (pageNumber !== searchItemRequest.pageNumber) {
      setSearchItemRequest((prev) => ({ ...prev, pageNumber }));
    }
  };

  const onPageSizeChange = async (pageSize: number) => {
    if (pageSize !== searchItemRequest.pageSize) {
      setSearchItemRequest((prev) => ({ ...prev, pageSize, pageNumber: 1 }));
    }
  };
  //#endregion

  //#region useEffect - load lookups once (parallel)
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [stores, categories] = await Promise.all([
          getLookupByType(LookupTypeEnum.Stores, true, false),
          getLookupByType(LookupTypeEnum.CategoryType, true, false),
        ]);

        setStoreLookup(stores || []);
        setCategoryLookup(categories || []);
      } catch (error) {
        console.error("Failed to load lookups", error);
        setStoreLookup([]);
        setCategoryLookup([]);
      }
    };

    loadLookups();
  }, []);
  //#endregion

  //#region useEffect - apply searchedFields to request (ONE place)
  useEffect(() => {
    const updated = mapSearchedFieldsToRequest(searchedFields);
    setSearchItemRequest((prev) => ({
      ...prev,
      ...updated,
      pageNumber: 1, // reset paging when search changes
    }));
  }, [searchedFields, mapSearchedFieldsToRequest]);
  //#endregion

  //#region useEffect - load items when request changes
  useEffect(() => {
    const fillData = async () => {
      setTableLoading(true);
      try {
        await getItems();
      } finally {
        setTableLoading(false);
      }
    };

    fillData();
  }, [
    getItems,
    searchItemRequest.pageNumber,
    searchItemRequest.pageSize,
    searchItemRequest.name,
    searchItemRequest.code,
    searchItemRequest.barCode,
    (searchItemRequest as any).storeId,
    (searchItemRequest as any).categoryId,
  ]);
  //#endregion

  //#region useEffect - external refresh
  useEffect(() => {
    if (!isRefresh) return;

    const refresh = async () => {
      setTableLoading(true);
      try {
        await getItems();
      } finally {
        setTableLoading(false);
        setIsRefresh(false);
      }
    };

    refresh();
  }, [isRefresh, getItems, setIsRefresh, setTableLoading]);
  //#endregion

  //#region html
  return (
      <LoadingBox isLoading={isTableLoading}>
        <PrivilegesChecker formId={PageEnum.Items} action="EnableSearch">
          <SearchBoxGeneric
              searchFields={searchFields}
              onChangeHandler={handleSearchFieldChange}
              onSearchHandler={handleSearch}
              onResetHandler={handleResetSearch}
              resetVersion={resetVersion}
          />
        </PrivilegesChecker>

        <GridTable
            data={data}
            totalRows={totalRows}
            currentPage={searchItemRequest.pageNumber || 1}
            pageSize={searchItemRequest.pageSize || defaultPageSize}
            onCurrentPageChange={onCurrentPageChange}
            onPageSizeChange={onPageSizeChange}
            paginationType="server"
            externalLoading={isTableLoading}
            columnsProps={{
              actions: [ActionTypeEnum.Update, ActionTypeEnum.Delete, ActionTypeEnum.Print],
              onActionEvent,
              isArabic,
            }}
            formId={PageEnum.Items}
        />
      </LoadingBox>
  );
  //#endregion
};
